/**
 * KAN-468 — Learner profile fields section ("My Information").
 *
 * Shows the active org's profile field definitions (standard seeded set +
 * instructor customs) with the learner's current values, editable inline.
 * Blank values render "Not provided". Birth month+year (fieldKey
 * `birth_month_year`) gets a month+year picker — deliberately no day — and
 * serializes to the contract's MM/YYYY wire format.
 *
 * Component orchestrates; logic lives in profile-fields.utils (FP patterns).
 * App is zoneless: every async mutation is followed by cdr.markForCheck().
 *
 * `/profile#profile-fields` (the dashboard "Complete profile" link): the router
 * has no anchorScrolling, and in a zoneless app the card is not in the DOM at
 * NavigationEnd anyway, so the component scrolls itself into view once its
 * fields have loaded.
 */
import {
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  inject,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription } from 'rxjs';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { ProfileFieldsService } from './profile-fields.service';
import {
  ProfileFieldWithValue,
  formatFieldValue,
  incompleteRequestedFields,
  isBlankValue,
  isMonthYearField,
  buildMonthYear,
  monthYearParts,
  toDateInputValue,
  validateProfileFieldValue,
  REQUESTED_FIELD_BADGE_LABEL,
} from './profile-fields.utils';

const CARD_FRAGMENT = 'profile-fields';

interface EditState {
  fieldId: string;
  /** Draft for plain input types. */
  draft: string;
  /** Draft parts for the month+year picker ('' = unselected). */
  draftMonth: string;
  draftYear: string;
  error: string | null;
  saving: boolean;
}

@Component({
  selector: 'app-profile-fields',
  standalone: true,
  imports: [CommonModule, FormsModule, IconComponent],
  templateUrl: './profile-fields.component.html',
  styleUrls: ['./profile-fields.component.scss'],
})
export class ProfileFieldsComponent implements OnInit, OnChanges {
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private profileFieldsService = inject(ProfileFieldsService);
  private hostRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private route = inject(ActivatedRoute, { optional: true });
  private scrollRequested = false;
  private initialized = false;
  private fieldsLoad?: Subscription;

  /**
   * KAN-562 follow-up: the badge on an outstanding field reads "Required",
   * because the course gate makes it exactly that for the learner. Display
   * label only - `isRequested` and `isRequired` are untouched.
   */
  readonly fieldBadgeLabel = REQUESTED_FIELD_BADGE_LABEL;

  /**
   * KAN-562: this section owns the field values, so it is the only place that
   * knows the moment the last requested field is filled. The profile page uses
   * it to offer the way back into a course the player gate turned the learner
   * away from. Emitted once the fields load, and again after every successful
   * save; deliberately silent when the load fails, so a failure can never read
   * as "all done".
   */
  @Output() requestedCompleteChange = new EventEmitter<boolean>();
  @Input() hasOrganization = true;

  fields: ProfileFieldWithValue[] = [];
  isLoading = true;
  loadError: string | null = null;
  edit: EditState | null = null;

  readonly months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  readonly years: number[] = (() => {
    const current = new Date().getFullYear();
    const years: number[] = [];
    for (let y = current; y >= 1920; y--) {
      years.push(y);
    }
    return years;
  })();

  ngOnInit(): void {
    this.route?.fragment
      ?.pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((fragment) => {
        this.scrollRequested = fragment === CARD_FRAGMENT;
      });

    this.initialized = true;
    this.syncOrganizationState();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.initialized && changes['hasOrganization']) this.syncOrganizationState();
  }

  private syncOrganizationState(): void {
    this.fieldsLoad?.unsubscribe();
    this.fields = [];
    this.edit = null;
    this.loadError = null;

    if (!this.hasOrganization) {
      this.isLoading = false;
      this.emitRequestedComplete();
      this.cdr.markForCheck();
      return;
    }

    this.isLoading = true;
    this.cdr.markForCheck();
    this.fieldsLoad = this.profileFieldsService
      .getMyProfileFields()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ fields }) => {
          this.fields = fields;
          this.isLoading = false;
          this.emitRequestedComplete();
          this.cdr.markForCheck();
          this.scrollToCardIfRequested();
        },
        error: () => {
          this.loadError = 'Unable to load your profile information.';
          this.isLoading = false;
          this.cdr.markForCheck();
        },
      });
  }

  displayValue(field: ProfileFieldWithValue): string {
    return formatFieldValue(field);
  }

  isRequestedAndBlank(field: ProfileFieldWithValue): boolean {
    return field.isRequested && isBlankValue(field.value);
  }

  isMonthYear(field: ProfileFieldWithValue): boolean {
    return isMonthYearField(field);
  }

  isEditing(field: ProfileFieldWithValue): boolean {
    return this.edit?.fieldId === field.fieldId;
  }

  inputType(field: ProfileFieldWithValue): string {
    switch (field.fieldType) {
      case 'number':
        return 'number';
      case 'date':
        return 'date';
      case 'email':
        return 'email';
      case 'phone':
        return 'tel';
      default:
        return 'text';
    }
  }

  startEdit(field: ProfileFieldWithValue): void {
    const parts = isMonthYearField(field) ? monthYearParts(field.value) : null;
    this.edit = {
      fieldId: field.fieldId,
      // <input type="date"> shows EMPTY for an ISO timestamp; it wants YYYY-MM-DD.
      draft: field.fieldType === 'date'
        ? toDateInputValue(field.value)
        : isBlankValue(field.value) ? '' : String(field.value),
      draftMonth: parts ? String(parts.month) : '',
      draftYear: parts ? String(parts.year) : '',
      error: null,
      saving: false,
    };
  }

  cancelEdit(): void {
    this.edit = null;
  }

  save(field: ProfileFieldWithValue): void {
    if (!this.edit || this.edit.saving) {
      return;
    }
    const value = this.draftValue(field);
    const candidate: ProfileFieldWithValue = { ...field, value };
    const error = validateProfileFieldValue(candidate);
    if (error) {
      this.edit = { ...this.edit, error };
      return;
    }

    this.edit = { ...this.edit, error: null, saving: true };
    this.profileFieldsService
      .updateMyFieldValue(field.fieldId, value)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (updated) => {
          this.fields = this.fields.map((f) =>
            f.fieldId === field.fieldId ? { ...f, ...updated } : f
          );
          this.edit = null;
          this.emitRequestedComplete();
          this.cdr.markForCheck();
        },
        error: (err) => {
          const message =
            err?.error?.error?.message ??
            err?.error?.message ??
            'Could not save this field. Please try again.';
          if (this.edit) {
            this.edit = { ...this.edit, error: message, saving: false };
          }
          this.cdr.markForCheck();
        },
      });
  }

  /** KAN-562: announce whether every org-requested field now has a value. */
  private emitRequestedComplete(): void {
    this.requestedCompleteChange.emit(incompleteRequestedFields(this.fields).length === 0);
  }

  /** One-shot scroll to the card once it has content, when the URL fragment asked for it. */
  private scrollToCardIfRequested(): void {
    if (!this.scrollRequested) {
      return;
    }
    this.scrollRequested = false;
    // Zoneless: force the loaded rows into the DOM before measuring the card.
    this.cdr.detectChanges();
    const card = this.hostRef.nativeElement.querySelector(`#${CARD_FRAGMENT}`) as HTMLElement | null;
    card?.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  /** Translate the current edit drafts into the wire value for this field. */
  private draftValue(field: ProfileFieldWithValue): unknown {
    if (!this.edit) {
      return null;
    }
    if (isMonthYearField(field)) {
      const { draftMonth, draftYear } = this.edit;
      return draftMonth !== '' && draftYear !== ''
        ? buildMonthYear(Number(draftMonth), Number(draftYear))
        : null;
    }
    const raw = this.edit.draft.trim();
    if (raw === '') {
      return null;
    }
    if (field.fieldType === 'number') {
      return Number(raw);
    }
    if (field.fieldType === 'boolean') {
      return raw === 'true';
    }
    return raw;
  }
}
